// Målestimering: fra én sensorpose (posisjon + asimut + pitch) til et treffpunkt
// på terrenget. Dette er «hvor peker kameraet, og hva traff strålen» — grunnlaget
// for at et bilde fra felt kan bli en markør i kartet i stedet for bare et punkt
// der fotografen sto.
//
// HVORFOR DENNE FILA FINNES (eierfunn 2026-08-17): et bilde sendt fra GNÅ ga
// posisjonen til FOTOGRAFEN, aldri til det han fotograferte. Eieren ba om
// «litt smartness — hvis vi kan estimere målet det er tatt bilde av og sette det
// i kartet». Se vale/SENSORLAG_MULIGHETSSTUDIE_2026-08-17.md.
//
// ⚠ DET VIKTIGSTE I HELE FILA: strålen treffer ofte IKKE noe.
// Et bilde tatt vannrett (pitch ≈ 0) gir en stråle som løper parallelt over
// bakken og aldri skjærer terrenget i flatt lende. Det finnes da INGEN
// målposisjon å regne ut — og å plassere en markør likevel ville vært presis den
// falske presisjonen grunnloven forbyr (§1). Derfor returnerer funksjonen en
// UNION: enten et treff med usikkerhet, eller en begrunnet ikke-treff som
// kalleren skal tegne som `Usikkerhetsform.sektor` (se track.ts) — sektoren er
// sann uansett pitch.
//
// DEM-data mates inn som `ElevationGrid` (terrain.ts), på samme måte som
// viewshed/siktlinje. Ren, deterministisk matematikk — ingen nettverk, ingen I/O.
import { haversineM, project } from './geo.js';
import { elevationAt } from './terrain.js';
/**
 * Jordradius (m) brukt til krumnings­korreksjon.
 */
const JORDRADIUS_M = 6_371_000;
/**
 * Refraksjonskoeffisient. Atmosfæren bøyer siktlinja litt NEDOVER mot jorda, så
 * den effektive jordkrumningen er mindre enn den geometriske. k ≈ 0,13 er
 * standard i landmåling («7/6-jorda»). Netto løft = (1-k)·d²/(2R).
 *
 * ⚠ AVVIK FRA `lineOfSight()` i terrain.ts, bevisst: den korrigerer IKKE for
 * krumning i det hele tatt. Det er forsvarlig for korte taktiske siktlinjer, men
 * ikke her: på 10 km er leddet 7,8 m, og et målestimat som ignorerer det legger
 * treffpunktet systematisk for NÆRT. Ulikheten er dokumentert framfor skjult;
 * å «rette» lineOfSight er en egen oppgave med egne tester.
 */
const REFRAKSJON_K = 0.13;
/** Øyehøyde (m) for en person som fotograferer, når ingenting annet er oppgitt. */
export const DEFAULT_OYEHOYDE_M = 1.6;
/** Hvor langt strålen følges før vi gir opp (m). */
export const DEFAULT_MAKS_AVSTAND_M = 15_000;
/**
 * Hvor mye en geometrisk RETT stråle stiger over havnivå-flata (m) etter
 * horisontal avstand `d`, som følge av at flata krummer bort under den.
 *
 * ⚠ FORTEGNET ER LETT Å SNU, OG Å SNU DET FLYTTER MÅLET FEIL VEI.
 * Vi regner i høyde-over-havet, som DEM-en også gjør. En rett linje som forlater
 * sensoren vannrett blir da HØYERE over havflata dess lenger den går — flata
 * bøyer nedover under den. Derfor LEGGES leddet til strålens høyde; det trekkes
 * ikke fra. Kontroll: en helt vannrett stråle skal da ALDRI treffe flatt lende,
 * som er fysisk riktig (horisonten ligger under vannrett, med dip-vinkelen
 * √(2h/R) — det er derfor man må se litt ned for å se havet møte himmelen).
 * Med motsatt fortegn ville en vannrett stråle truffet ved horisontavstanden, og
 * alle mål blitt lagt for nært.
 */
function krumningsloft(d, aktiv) {
    if (!aktiv)
        return 0;
    return ((1 - REFRAKSJON_K) * d * d) / (2 * JORDRADIUS_M);
}
/**
 * Skyt én stråle fra sensoren og finn der den skjærer terrenget.
 *
 * Marsjerer langs bakken i skritt på DEM-cellestørrelse, sammenligner strålens
 * høyde med terrenget i hvert skritt, og forfiner skjæringen med halvering
 * (bisection) når den er innelukket mellom to skritt. Halveringen er ikke pynt:
 * uten den ligger treffpunktet på nærmeste hele skritt, altså kvantisert til
 * DEM-oppløsningen (typisk 30 m) selv når geometrien er mye bedre kjent.
 */
function skytStraale(g, pose, opts = {}) {
    const maks = opts.maksAvstandM ?? DEFAULT_MAKS_AVSTAND_M;
    const krumning = opts.krumning ?? true;
    // Aldri finere enn dataen, og aldri så grovt at en rygg forsvinner mellom to
    // skritt. `cellSizeM` er DEM-ens egen oppløsning og dermed riktig default.
    const skritt = Math.max(1, opts.skrittM ?? g.cellSizeM);
    const origo = { lat: pose.lat, lon: pose.lon };
    // Starthøyde: absolutt når den finnes (drone/GPS), ellers DEM + høyde over bakken.
    let startHoyde;
    if (pose.hoydeMslM !== undefined) {
        startHoyde = pose.hoydeMslM;
    }
    else {
        const bakke = elevationAt(g, pose.lat, pose.lon);
        if (bakke === null) {
            return {
                kind: 'ingen-treff',
                grunn: 'ingen-hoyde-ved-sensor',
                forklaring: 'Høydedata mangler der sensoren står, så strålens starthøyde er ukjent. ' +
                    'Retningen er fortsatt gyldig — vis den som sektor.',
                fulgtTilM: 0,
            };
        }
        startHoyde = bakke + (pose.hoydeOverTerrengM ?? DEFAULT_OYEHOYDE_M);
    }
    // ⚠ INGEN TIDLIG RETUR PÅ pitch ≥ 0.
    // Første utgave av denne fila avbrøt her med «peker-opp» så snart pitch ikke var
    // negativ. Det var GALT, og feilen ville rammet det vanligste taktiske bildet vi
    // har: en telefon holdt vannrett mot en fjellside 500 m unna TREFFER fjellsiden.
    // Om strålen skjærer terrenget avgjøres av om terrenget stiger inn i den — ikke
    // av fortegnet på pitch. Vi marsjerer derfor alltid, og lar lendet svare.
    // «peker-opp» er en KONKLUSJON etter at strålen ikke traff, ikke en forutsetning.
    const tanPitch = Math.tan((pose.pitchDeg * Math.PI) / 180);
    /** Strålens høyde over havet ved horisontal avstand d. */
    const straaleHoyde = (d) => startHoyde + d * tanPitch + krumningsloft(d, krumning);
    let forrigeD = 0;
    for (let d = skritt; d <= maks; d += skritt) {
        const p = project(origo, pose.asimutDeg, d);
        const terreng = elevationAt(g, p.lat, p.lon);
        if (terreng === null) {
            return {
                kind: 'ingen-treff',
                grunn: 'utenfor-dem',
                forklaring: `Strålen forlot høydedekningen etter ${Math.round(d)} m uten å treffe terrenget. ` +
                    'Hent et større høydeutsnitt for å estimere målet.',
                fulgtTilM: d,
            };
        }
        const diff = straaleHoyde(d) - terreng; // > 0 = strålen er over bakken
        if (diff <= 0) {
            // Skjæringen ligger mellom forrigeD og d. Halver til under en meter.
            let lo = forrigeD;
            let hi = d;
            for (let i = 0; i < 40 && hi - lo > 0.5; i++) {
                const mid = (lo + hi) / 2;
                const pm = project(origo, pose.asimutDeg, mid);
                const tm = elevationAt(g, pm.lat, pm.lon);
                if (tm === null)
                    break; // hull midt i intervallet: behold grovt treff
                if (straaleHoyde(mid) - tm <= 0)
                    hi = mid;
                else
                    lo = mid;
            }
            const treffD = hi;
            const treff = project(origo, pose.asimutDeg, treffD);
            const treffHoyde = elevationAt(g, treff.lat, treff.lon) ?? terreng;
            return {
                kind: 'treff',
                lat: treff.lat,
                lon: treff.lon,
                terrengHoydeM: treffHoyde,
                avstandM: treffD,
                // Fylles av `estimerMaal` — `skytStraale` alene kjenner ikke spredningen.
                usikkerhet: { storAkseM: 0, lilleAkseM: 0, retningDeg: 0, cepM: 0 },
            };
        }
        void diff;
        forrigeD = d;
    }
    // Strålen nådde maks uten å treffe. NÅ kan vi skille de to årsakene, og
    // skillet er operativt: «peker-opp» betyr at ingen rekkevidde i verden hjelper,
    // mens «over-maks-avstand» betyr at et større søk kanskje ville truffet.
    if (pose.pitchDeg >= 0) {
        return {
            kind: 'ingen-treff',
            grunn: 'peker-opp',
            forklaring: `Kameraet peker ${pose.pitchDeg === 0 ? 'vannrett' : 'oppover'} ` +
                `(pitch ${pose.pitchDeg.toFixed(1)}°), og ingenting i lendet stiger inn i ` +
                'strålen. Avstanden til målet kan ikke beregnes — retningen er kjent, ' +
                'så vis den som sektor.',
            fulgtTilM: maks,
        };
    }
    return {
        kind: 'ingen-treff',
        grunn: 'over-maks-avstand',
        forklaring: `Strålen ble fulgt ${Math.round(maks)} m uten å skjære terrenget — ` +
            'kameraet peker for flatt, eller utover et lavere lende (dal, fjord, sjø). ' +
            'Retningen er kjent — vis den som sektor.',
        fulgtTilM: maks,
    };
}
/**
 * ⚠ HVOR DENNE FUNKSJONEN IKKE DUGER: MÅL PÅ VANN, NÆR HORISONTEN.
 *
 * `pitchDeg` er en OPPGITT vinkel, og på en telefon kommer den fra
 * gravitasjonsvektoren med typisk **1,7° feil**. Det er brukbart mot et mål i
 * kupert lende, der strålen møter terrenget bratt. Det er ubrukelig over vann:
 *
 *   Fra 2 m øyehøyde er HELE måleområdet 0 → 5,4 km presset inn i **2,54
 *   bueminutter** depresjon. 1,7° er 102 bueminutter — altså **40× hele
 *   budsjettet**. IMU-pitch over vann er ikke en måling, det er en gjetning.
 *
 * Feilen skalerer som d²/h, så den blir verre med avstand og bedre med høyde:
 * fra 30 m har man 8 bueminutter og ~2 % relativ feil i stedet for ~17 %.
 *
 * RIKTIG LØSNING for maritime mål er å hente pitch (og roll) UT AV BILDET ved å
 * fitte HORISONTLINJA: den strekker seg over tusenvis av piksler, gir sub-0,2 px
 * linjefit, og eliminerer både pitch- og rollfeil fullstendig. Da er havflaten
 * selv referanseplanet, og ingen høydemodell trengs. Se
 * `vale/MAALPEKING_KONSEPT_2026-08-17.md` §1 og kort MAAL-03.
 *
 * Denne funksjonen er altså RIKTIG for det den gjør (stråle mot terreng med
 * oppgitt pose), men kallstedet må vite at posen er den svake lenken — og over
 * vann er den så svak at svaret ikke bør vises som et punkt i det hele tatt.
 * Usikkerhets-ellipsen fanger det når `pitchSigmaDeg` settes ærlig: oppgi 1,7°
 * for IMU-pitch, og se hvor stor storaksen blir.
 */
/**
 * Estimer målet et bilde er tatt av: skyt strålen, og propagér sensorusikkerheten
 * NUMERISK ved å skyte den på nytt i ytterkantene av asimut og pitch.
 *
 * HVORFOR NUMERISK OG IKKE ANALYTISK: den lukkede formelen for avstandsfeil
 * (`h/sin²(pitch)`) gjelder bare over en flat, vannrett bakke. I ekte lende
 * bestemmes spredningen av hvor strålen treffer en skråning — en 1° endring kan
 * flytte treffpunktet 5 m opp en bratt fjellside eller 800 m bortover en slette.
 * Å skyte to ekstra stråler koster ~2× og gir svaret terrenget faktisk gir, i
 * stedet for et tall fra en modell terrenget ikke følger.
 *
 * Returnerer `ingen-treff` uendret når senterstrålen ikke traff — kalleren skal
 * da bygge en `Usikkerhetsform.sektor` av asimut og `fulgtTilM`.
 */
export function estimerMaal(g, pose, opts = {}) {
    const senter = skytStraale(g, pose, opts);
    if (senter.kind !== 'treff')
        return senter;
    const asimutSigma = pose.asimutSigmaDeg ?? 0;
    const pitchSigma = pose.pitchSigmaDeg ?? 0;
    const senterPunkt = { lat: senter.lat, lon: senter.lon };
    // LANGS strålen: pitch ± 1σ. Vi tar det STØRSTE av de to utslagene, ikke
    // gjennomsnittet — feilen er ikke symmetrisk (flatere pitch flytter treffet
    // mye lenger ut enn brattere pitch flytter det inn), og et snitt ville
    // underrapportert den retningen som er verst.
    let storAkseM = 0;
    if (pitchSigma > 0) {
        for (const dp of [-pitchSigma, pitchSigma]) {
            const p = skytStraale(g, { ...pose, pitchDeg: pose.pitchDeg + dp }, opts);
            if (p.kind === 'treff') {
                storAkseM = Math.max(storAkseM, haversineM(senterPunkt, { lat: p.lat, lon: p.lon }));
            }
            else {
                // Én av kantene treffer ikke i det hele tatt: usikkerheten er da i praksis
                // ubegrenset utover. Vi oppgir avstanden vi HAR som nedre grense, framfor
                // å late som spredningen er liten fordi den ikke kunne måles.
                storAkseM = Math.max(storAkseM, senter.avstandM);
            }
        }
    }
    // PÅ TVERS: asimut ± 1σ. Denne er nær-lineær (bue­lengde = avstand · vinkel),
    // men vi måler den likevel, så et treff på en skråning til siden fanges opp.
    let lilleAkseM = 0;
    if (asimutSigma > 0) {
        for (const da of [-asimutSigma, asimutSigma]) {
            const p = skytStraale(g, { ...pose, asimutDeg: (pose.asimutDeg + da + 360) % 360 }, opts);
            if (p.kind === 'treff') {
                lilleAkseM = Math.max(lilleAkseM, haversineM(senterPunkt, { lat: p.lat, lon: p.lon }));
            }
            else {
                // Fall tilbake på ren buelengde: retningsfeilen er kjent selv når
                // sidestrålen bommer på terrenget.
                lilleAkseM = Math.max(lilleAkseM, senter.avstandM * Math.tan((asimutSigma * Math.PI) / 180));
            }
        }
    }
    return {
        ...senter,
        usikkerhet: {
            storAkseM,
            lilleAkseM,
            // Storaksen ligger LANGS strålen, altså i asimutretningen. Aksen er
            // symmetrisk, så 0–180 er nok (jf. Usikkerhetsform.ellipse).
            retningDeg: pose.asimutDeg % 180,
            cepM: Math.max(storAkseM, lilleAkseM),
        },
    };
}
/**
 * `Treffusikkerhet` som lukket ring ([lon,lat]-par), til kartlaget. Samme
 * parametriske ellipse-konstruksjon som `krysspeilingEllipseRing` i
 * krakenrf.ts (storakse/lilleakse/orientering → rotert ellipse rundt et
 * senter) — faktorert hit fordi `Treffusikkerhet` er FORMEN alle ranging-
 * metodene deler (stråle-mot-DEM her, horisont+havflate og landemerke-
 * resection i `maalpeking.ts`, bølge MAAL-01). Én tegnefunksjon for ellipsen,
 * ikke en per metode — samme resonnement som gjorde `Treffusikkerhet` selv til
 * en delt type i stedet for at hver fil fant opp sin egen usikkerhetsform.
 */
export function treffusikkerhetRing(senter, u, kSigma = 1, steg = 48) {
    if (!(u.storAkseM > 0) && !(u.lilleAkseM > 0))
        return [];
    const a = u.storAkseM * kSigma;
    const b = u.lilleAkseM * kSigma;
    const ring = [];
    for (let i = 0; i <= steg; i++) {
        const t = (2 * Math.PI * i) / steg;
        // Parametrisk ellipse i (storakse, lilleakse), rotert til retningDeg.
        const langs = a * Math.cos(t);
        const tvers = b * Math.sin(t);
        const r = Math.hypot(langs, tvers);
        const vinkel = u.retningDeg + (Math.atan2(tvers, langs) * 180) / Math.PI;
        const p = project(senter, vinkel, r);
        ring.push([p.lon, p.lat]);
    }
    return ring;
}
//# sourceMappingURL=maalestimat.js.map